import { useState } from 'react'
import { Plus, Trash2, FileText, Link2, FolderDown, ExternalLink } from 'lucide-react'
import { useCollection, useFileUpload } from '../../hooks/useFirestore'
import { deleteFile } from '../../services/firebase'
import { formatFileSize, formatRelativeTime } from '../../utils/helpers'
import { Button, Modal, Input, Textarea, Badge, Checkbox } from '../../components/common'

const ResourceForm = ({ departments, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'file',
    url: '',
    storagePath: null,
    fileName: '',
    fileSize: null,
    departmentIds: [],
  })
  const [saving, setSaving] = useState(false)
  const { upload, uploading } = useFileUpload()

  const toggleDept = (slug) => {
    const next = formData.departmentIds.includes(slug)
      ? formData.departmentIds.filter((s) => s !== slug)
      : [...formData.departmentIds, slug]
    setFormData({ ...formData, departmentIds: next })
  }

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const path = `resources/${Date.now()}_${file.name}`
    const result = await upload(file, path)
    if (result.success) {
      setFormData({
        ...formData,
        url: result.url,
        storagePath: path,
        fileName: file.name,
        fileSize: file.size,
        title: formData.title || file.name.replace(/\.[^.]+$/, ''),
      })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.url) return
    setSaving(true)
    await onSave(formData)
    setSaving(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex gap-2">
        {['file', 'link'].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setFormData({ ...formData, type: t, url: '', storagePath: null })}
            className={`px-4 py-2 rounded-lg font-medium capitalize transition-colors ${
              formData.type === t
                ? 'bg-primary-900 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t === 'file' ? 'Upload File' : 'External Link'}
          </button>
        ))}
      </div>

      {formData.type === 'file' ? (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Document</label>
          <div className="flex items-center gap-4">
            {formData.fileName && (
              <Badge variant="info" size="sm" icon={FileText}>
                {formData.fileName}
              </Badge>
            )}
            <label className="px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
              <span className="text-sm">{uploading ? 'Uploading...' : 'Choose File'}</span>
              <input type="file" onChange={handleFileUpload} className="hidden" />
            </label>
          </div>
        </div>
      ) : (
        <Input
          label="URL"
          type="url"
          required
          value={formData.url}
          onChange={(e) => setFormData({ ...formData, url: e.target.value })}
          placeholder="https://..."
        />
      )}

      <Input
        label="Title"
        required
        value={formData.title}
        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        placeholder="Choir handbook"
      />

      <Textarea
        label="Description (optional)"
        rows={2}
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
      />

      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">
          Visible to (none selected = all workers)
        </p>
        <div className="flex flex-wrap gap-4">
          {departments.map((dept) => (
            <Checkbox
              key={dept.id}
              label={dept.name}
              checked={formData.departmentIds.includes(dept.id)}
              onChange={() => toggleDept(dept.id)}
            />
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={saving} disabled={!formData.url}>
          Add Resource
        </Button>
      </div>
    </form>
  )
}

const ResourcesManager = () => {
  const [showModal, setShowModal] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const { data: resources, add, remove } = useCollection('resources')
  const { data: departments } = useCollection('departments', {
    orderByField: 'order',
    orderDirection: 'asc',
  })

  const deptName = (slug) => departments?.find((d) => d.id === slug)?.name || slug

  const handleSave = async (data) => {
    await add(data)
    setShowModal(false)
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return
    if (deleteConfirm.storagePath) {
      try {
        await deleteFile(deleteConfirm.storagePath)
      } catch (err) {
        console.warn('Could not delete stored file:', err)
      }
    }
    await remove(deleteConfirm.id)
    setDeleteConfirm(null)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-dark-100">Resources</h1>
          <p className="text-gray-500">Documents and links shared with workers</p>
        </div>
        <Button variant="primary" icon={Plus} onClick={() => setShowModal(true)}>
          Add Resource
        </Button>
      </div>

      <div className="space-y-3">
        {(resources || []).map((r) => (
          <div key={r.id} className="bg-white rounded-xl shadow-lg p-5 flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                {r.type === 'link'
                  ? <Link2 className="w-5 h-5 text-primary-600" />
                  : <FileText className="w-5 h-5 text-primary-600" />}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-dark-100 hover:text-primary-600 flex items-center gap-1"
                  >
                    {r.title}
                    <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                  </a>
                  {(r.departmentIds || []).map((slug) => (
                    <Badge key={slug} variant="primary" size="sm">{deptName(slug)}</Badge>
                  ))}
                  {(r.departmentIds || []).length === 0 && (
                    <Badge variant="gray" size="sm">All workers</Badge>
                  )}
                </div>
                {r.description && <p className="text-sm text-gray-600 mt-1">{r.description}</p>}
                <p className="text-xs text-gray-400 mt-1">
                  {r.fileSize ? `${formatFileSize(r.fileSize)} · ` : ''}
                  {r.createdAt ? formatRelativeTime(r.createdAt?.toDate ? r.createdAt.toDate() : r.createdAt) : ''}
                </p>
              </div>
            </div>
            <button
              onClick={() => setDeleteConfirm(r)}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        ))}

        {(resources || []).length === 0 && (
          <div className="bg-white rounded-xl shadow-lg text-center py-12 text-gray-500">
            <FolderDown className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No resources yet</p>
          </div>
        )}
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Add Resource"
        size="lg"
      >
        <ResourceForm
          departments={departments || []}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
        />
      </Modal>

      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Resource"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Delete "{deleteConfirm?.title}"?
          {deleteConfirm?.storagePath ? ' The uploaded file will also be removed.' : ''}
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default ResourcesManager
